import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { contactInfo, faqData } from '../data/mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contacts = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: 'Повідомлення відправлено!',
        description: 'Ми зв\'яжемося з вами найближчим часом.',
      });
      
      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Помилка',
        description: 'Не вдалося відправити повідомлення. Спробуйте пізніше або зателефонуйте нам.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Зв'язатися з нами
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Маєте питання чи хочете отримати консультацію? Зв'яжіться з нами у зручний для вас спосіб.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Контактна інформація</h2>
                
                <div className="space-y-6">
                  <Card className="border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(1, 157, 180, 0.1)' }}>
                          <Phone className="h-6 w-6" style={{ color: 'rgb(1, 157, 180)' }} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Телефон</h3>
                          <div className="flex flex-col space-y-1">
                            {contactInfo.phones.map((phone, index) => (
                              <a
                                key={index}
                                href={`tel:${phone.link}`}
                                className="text-gray-600 hover:text-[rgb(1,157,180)] transition-colors"
                              >
                                {phone.number}
                              </a>
                            ))}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{contactInfo.contact}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(1, 157, 180, 0.1)' }}>
                          <Mail className="h-6 w-6" style={{ color: 'rgb(1, 157, 180)' }} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                          <a
                            href={`mailto:${contactInfo.email}`}
                            className="text-gray-600 hover:text-[rgb(1,157,180)] transition-colors break-all"
                          >
                            {contactInfo.email}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(1, 157, 180, 0.1)' }}>
                          <MapPin className="h-6 w-6" style={{ color: 'rgb(1, 157, 180)' }} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Адреса</h3>
                          <p className="text-gray-600">{contactInfo.address}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(1, 157, 180, 0.1)' }}>
                          <Clock className="h-6 w-6" style={{ color: 'rgb(1, 157, 180)' }} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Графік роботи</h3>
                          <p className="text-gray-600">{contactInfo.workingHours}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Форма зворотного зв'язку</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Ім\'я *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Ваше ім\'я"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="phone">Телефон *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="+380 XX XXX XX XX"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="email@example.com"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="subject">Тема звернення</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Тема повідомлення"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Повідомлення *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Опишіть ваше питання або проєкт..."
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full text-white hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'rgb(1, 157, 180)' }}
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Відправити повідомлення
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Часті питання
            </h2>
            <p className="text-lg text-gray-600">
              Відповіді на найпоширеніші питання
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`faq-${faq.id}`}
                className="bg-white rounded-lg shadow-md border-none px-6"
              >
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'rgb(1, 157, 180)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Не знайшли відповідь?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Зателефонуйте нам, і ми з радістю відповімо на всі ваші питання
          </p>
          <Button asChild size="lg" variant="secondary" className="bg-white text-gray-900 hover:bg-gray-100">
            <a href={`tel:${contactInfo.phoneLink}`}>
              <Phone className="mr-2 h-4 w-4" />
              {contactInfo.phone}
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};